import { useState, useRef, useEffect } from 'react';
import { Camera, Square, Download, Mic } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Field } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';

export function ScreenRecorder() {
  const [recording, setRecording] = useState(false);
  const [withAudio, setWithAudio] = useState(true);
  const [withMic, setWithMic] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [outUrl, setOutUrl] = useState('');
  const [outBlob, setOutBlob] = useState<Blob | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      stopAll();
      if (outUrl) URL.revokeObjectURL(outUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stopAll() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (tickRef.current) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }

  async function start() {
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: withAudio,
      });
      let combined = display;
      if (withMic) {
        try {
          const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
          combined = new MediaStream([
            ...display.getVideoTracks(),
            ...display.getAudioTracks(),
            ...mic.getAudioTracks(),
          ]);
        } catch {
          toast.error('Microphone access denied');
        }
      }
      streamRef.current = combined;
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';
      const rec = new MediaRecorder(combined, { mimeType: mime });
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setOutBlob(blob);
        setOutUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
        stopAll();
        setRecording(false);
      };
      // Auto-stop when user ends the screen share via browser UI
      display.getVideoTracks()[0].onended = () => {
        if (rec.state === 'recording') rec.stop();
      };
      rec.start(1000);
      recorderRef.current = rec;
      setRecording(true);
      setElapsed(0);
      startRef.current = Date.now();
      tickRef.current = window.setInterval(() => {
        setElapsed(Date.now() - startRef.current);
      }, 200);
    } catch {
      toast.error('Recording cancelled');
    }
  }

  function stop() {
    recorderRef.current?.stop();
  }

  function fmt(ms: number): string {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  }

  return (
    <div>
      <ToolHeader
        toolId="screen-recorder"
        title="Screen recorder"
        description="Record your screen, window, or browser tab. Save as WebM. Optional system audio + mic mix."
        badges={
          recording ? (
            <Badge variant="danger">
              <span className="size-1.5 rounded-full bg-danger animate-pulse mr-1" />
              REC · {fmt(elapsed)}
            </Badge>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          {!recording ? (
            <>
              <Field label="Audio">
                <label className="flex items-center justify-between text-sm cursor-pointer">
                  <span className="text-fg-muted">System audio</span>
                  <Switch checked={withAudio} onChange={setWithAudio} />
                </label>
                <label className="flex items-center justify-between text-sm cursor-pointer mt-2">
                  <span className="text-fg-muted flex items-center gap-1.5">
                    <Mic size={11} />
                    Microphone
                  </span>
                  <Switch checked={withMic} onChange={setWithMic} />
                </label>
              </Field>
              <Button variant="primary" className="w-full" onClick={start}>
                <Camera size={12} />
                Start recording
              </Button>
              <div className="text-2xs text-fg-subtle leading-relaxed">
                You'll choose the screen, window, or tab to share. Recording stops automatically when you end the share.
              </div>
            </>
          ) : (
            <>
              <div className="text-center py-4">
                <div className="font-mono text-3xl tabular-nums">{fmt(elapsed)}</div>
                <div className="text-2xs text-fg-subtle uppercase tracking-wider mt-1">Recording</div>
              </div>
              <Button variant="danger" className="w-full" onClick={stop}>
                <Square size={12} />
                Stop recording
              </Button>
            </>
          )}

          {outBlob && !recording && (
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => saveAs(outBlob, `recording-${new Date().toISOString().slice(0, 10)}.webm`)}
            >
              <Download size={12} />
              Save WebM
            </Button>
          )}
        </Card>

        <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            {outUrl ? 'Recording' : 'Preview'}
          </div>
          <div className="bg-black min-h-[420px] flex items-center justify-center">
            {outUrl ? (
              <video
                src={outUrl}
                controls
                className="max-w-full max-h-[460px]"
              />
            ) : (
              <div className="text-fg-subtle text-sm">
                Recording will appear here when finished.
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default ScreenRecorder;
